import { useEffect, useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight, Plus, Trash2 } from 'lucide-react';
import { z } from 'zod';
import { addDays, today } from '@mkcore/shared';
import { createMileage, deleteMileage, restoreMileage, updateBillingSettings } from '@mkcore/billing/schema';
import { Badge, Button, Card, Checkbox, EmptyState, ErrorState, Field, Input, PageHeader, Skeleton, TBody, THead, Table, Td, Th, Tr, toast } from '@mkcore/ui';
import { ApiError, newIdempotencyKey } from '@/lib/api';
import { formatDate, formatMoney, formatNumber } from '@/lib/format';
import { useActionMutation, useErrorMessage, UNDO_TOAST_MS } from '@/lib/mutation';
import { useCan, useMe } from '@/lib/session';
import { billingSettingsQuery, mileageQuery } from '@/features/billing/queries';
import { ProjectSelect } from '@/features/time/pickers';

const search = z.object({ month: z.string().optional(), entry: z.string().optional() });
export const Route = createFileRoute('/_app/billing/mileage')({ validateSearch: search, component: Mileage });

const monthOf = (d: string) => d.slice(0, 7);
const monthStart = (m: string) => `${m}-01`;
const monthEnd = (m: string) => {
  const y = Number(m.slice(0, 4));
  const mo = Number(m.slice(5, 7));
  return addDays(mo === 12 ? `${y + 1}-01-01` : `${y}-${String(mo + 1).padStart(2, '0')}-01`, -1);
};
const shiftMonth = (m: string, by: number) => {
  const y = Number(m.slice(0, 4));
  const mo = Number(m.slice(5, 7)) - 1 + by;
  return `${y + Math.floor(mo / 12)}-${String(((mo % 12) + 12) % 12 + 1).padStart(2, '0')}`;
};
const MONTHS_NL = ['januari', 'februari', 'maart', 'april', 'mei', 'juni', 'juli', 'augustus', 'september', 'oktober', 'november', 'december'];

/** The mileage log per month (CO-0370): an entry row on top, the trips with totals, and the rate for owner and admin. */
function Mileage() {
  const { t } = useTranslation();
  const s = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const message = useErrorMessage();
  const me = useMe();
  const canCreate = useCan('billing:create');
  const canManage = useCan('billing:manage');
  const month = s.month ?? monthOf(today());
  const q = useInfiniteQuery(mileageQuery({ from: monthStart(month), to: monthEnd(month), limit: 100 }));
  const rows = q.data?.pages.flatMap((p) => p.items) ?? [];
  const totals = q.data?.pages[0]?.totals;
  const invalidate = [['billing', 'mileage']];
  const restore = useActionMutation(restoreMileage, { invalidate, success: t('mileage.restored') });
  const remove = useActionMutation(deleteMileage, {
    invalidate,
    silentErrors: true,
    onSuccess: (e) => toast(t('mileage.deleted'), { duration: UNDO_TOAST_MS, action: { label: t('common.restore'), onClick: () => restore.mutate({ input: { id: e.id } }) } }),
  });
  const monthLabel = `${MONTHS_NL[Number(month.slice(5, 7)) - 1]} ${month.slice(0, 4)}`;
  return (
    <>
      <PageHeader title={t('mileage.title')} description={t('mileage.description')} />
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Button variant="ghost" size="icon" aria-label={t('mileage.prevMonth')} onClick={() => void navigate({ search: { month: shiftMonth(month, -1) }, replace: true })}>
          <ChevronLeft />
        </Button>
        <span className="min-w-40 text-sm font-bold capitalize">{monthLabel}</span>
        <Button variant="ghost" size="icon" aria-label={t('mileage.nextMonth')} onClick={() => void navigate({ search: { month: shiftMonth(month, 1) }, replace: true })}>
          <ChevronRight />
        </Button>
        <Button variant="secondary" size="sm" disabled={month === monthOf(today())} onClick={() => void navigate({ search: {}, replace: true })}>
          {t('mileage.thisMonth')}
        </Button>
        {totals && (
          <div className="ml-auto flex flex-wrap gap-x-5 text-sm text-fg-2" data-testid="mileage-totals">
            <span>
              {t('mileage.km')}: <span className="font-bold tabular-nums text-fg-1">{formatNumber(totals.distanceMeters / 1000, me.locale, 1)}</span>
            </span>
            <span>
              {t('mileage.amount')}: <span className="font-bold tabular-nums text-fg-1">{formatMoney(totals.amountCents)}</span>
            </span>
            <span>
              {t('mileage.billableAmount')}: <span className="font-bold tabular-nums text-fg-1">{formatMoney(totals.billableAmountCents)}</span>
            </span>
          </div>
        )}
      </div>
      {canCreate && <NewTrip month={month} />}
      {q.isError ? (
        <ErrorState title={t('errors.generic')} description={message(q.error)} />
      ) : q.isLoading ? (
        <Skeleton className="h-48" />
      ) : !rows.length ? (
        <EmptyState title={t('mileage.empty')} description={canCreate ? t('mileage.emptyHint') : undefined} />
      ) : (
        <Table>
          <THead>
            <Tr>
              <Th>{t('time.fields.date')}</Th>
              <Th>{t('mileage.fields.purpose')}</Th>
              <Th className="hidden md:table-cell">{t('mileage.fields.route')}</Th>
              <Th className="hidden sm:table-cell">{t('time.fields.project')}</Th>
              <Th className="text-right">{t('mileage.km')}</Th>
              <Th className="text-right">{t('mileage.amount')}</Th>
              <Th className="sr-only">{t('common.actions')}</Th>
            </Tr>
          </THead>
          <TBody>
            {rows.map((e) => (
              <Tr key={e.id} className={s.entry === e.id ? 'ring-2 ring-gold' : undefined}>
                <Td className="tabular-nums">{formatDate(e.date)}</Td>
                <Td>
                  {e.purpose} {e.billable ? <Badge tone="success">{t('mileage.billable')}</Badge> : null}
                  {e.locked ? (
                    <Badge tone="neutral" className="ml-1">
                      {t(`mileage.status.${e.status}`)}
                    </Badge>
                  ) : null}
                </Td>
                <Td className="hidden text-fg-2 md:table-cell">{e.fromPlace || e.toPlace ? `${e.fromPlace ?? '…'} → ${e.toPlace ?? '…'}` : ''}</Td>
                <Td className="hidden sm:table-cell">
                  {e.projectName ?? e.engagementName ?? e.organisationName ?? <span className="text-fg-3">{t('mileage.noClient')}</span>}
                  {e.projectName && e.organisationName ? <span className="block text-xs text-fg-3">{e.organisationName}</span> : null}
                </Td>
                <Td className="text-right tabular-nums">{formatNumber(e.distanceKm, me.locale, 1)}</Td>
                <Td className="text-right tabular-nums">{formatMoney(e.amountCents)}</Td>
                <Td className="text-right">
                  {!e.locked && (e.userId === me.id || canManage) && (
                    <Button size="icon" variant="ghost" aria-label={t('common.delete')} onClick={() => remove.mutate({ input: { id: e.id } }, { onError: (err) => toast.error(message(err)) })}>
                      <Trash2 />
                    </Button>
                  )}
                </Td>
              </Tr>
            ))}
          </TBody>
        </Table>
      )}
      {q.hasNextPage && (
        <div className="mt-3 flex justify-center">
          <Button variant="secondary" loading={q.isFetchingNextPage} onClick={() => void q.fetchNextPage()}>
            {t('common.more')}
          </Button>
        </div>
      )}
      <RatePanel />
    </>
  );
}

/** The entry row: date, kilometres, purpose, project and billable; Enter books. */
function NewTrip({ month }: { month: string }) {
  const { t } = useTranslation();
  const message = useErrorMessage();
  const todayDate = today();
  const [date, setDate] = useState(month === monthOf(todayDate) ? todayDate : monthStart(month));
  const [km, setKm] = useState('');
  const [purpose, setPurpose] = useState('');
  const [projectId, setProjectId] = useState('');
  const [billable, setBillable] = useState<boolean | null>(null);
  const [key, setKey] = useState(newIdempotencyKey);
  const [errors, setErrors] = useState<Record<string, string>>({});
  useEffect(() => setDate(month === monthOf(todayDate) ? todayDate : monthStart(month)), [month, todayDate]);
  const create = useActionMutation(createMileage, {
    invalidate: [['billing', 'mileage']],
    silentErrors: true,
    success: t('mileage.booked'),
    onSuccess: () => {
      setKm('');
      setPurpose('');
      setErrors({});
      setKey(newIdempotencyKey());
    },
  });
  const submit = () => {
    const distanceKm = Number(km.replace(',', '.'));
    if (!Number.isFinite(distanceKm) || distanceKm <= 0) {
      setErrors({ distanceKm: t('mileage.kmInvalid') });
      return;
    }
    create.mutate(
      { input: { date, distanceKm: Math.round(distanceKm * 10) / 10, purpose, ...(projectId && { projectId }), ...(billable !== null && { billable }) }, key },
      { onError: (e) => setErrors(e instanceof ApiError ? { ...e.fieldErrors(), _: message(e) } : { _: message(e) }) },
    );
  };
  return (
    <Card className="mb-4 p-3">
      <form
        className="flex flex-wrap items-end gap-3"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <Field label={t('time.fields.date')} className="w-40" error={errors.date}>{(p) => <Input {...p} type="date" value={date} onChange={(e) => setDate(e.target.value)} />}</Field>
        <Field label={t('mileage.km')} className="w-28" error={errors.distanceKm}>{(p) => <Input {...p} inputMode="decimal" placeholder="12,5" value={km} onChange={(e) => setKm(e.target.value)} />}</Field>
        <Field label={t('mileage.fields.purpose')} className="min-w-56 flex-1" error={errors.purpose}>{(p) => <Input {...p} value={purpose} placeholder={t('mileage.fields.purposeHint')} onChange={(e) => setPurpose(e.target.value)} />}</Field>
        <Field label={t('time.fields.project')} className="w-56" error={errors.projectId}>{(p) => <ProjectSelect {...p} value={projectId} emptyLabel={t('mileage.noClient')} onChange={setProjectId} />}</Field>
        <label className="flex items-center gap-2 pb-2 text-sm">
          <Checkbox checked={billable ?? !!projectId} onCheckedChange={(v) => setBillable(v === true)} /> {t('mileage.billable')}
        </label>
        <Button type="submit" loading={create.isPending} disabled={!km || !purpose.trim()}>
          <Plus /> {t('mileage.book')}
        </Button>
        {errors._ && (
          <p role="alert" className="basis-full text-sm text-danger">
            {errors._}
          </p>
        )}
      </form>
    </Card>
  );
}

/** The mileage rate of the company, for owner and admin. */
function RatePanel() {
  const { t } = useTranslation();
  const canManage = useCan('billing:manage');
  const q = useQuery(billingSettingsQuery);
  const [value, setValue] = useState<string | null>(null);
  const save = useActionMutation(updateBillingSettings, { invalidate: [['billing']], success: t('common.saved'), onSuccess: () => setValue(null) });
  if (!q.data) return null;
  const shown = value ?? (q.data.mileageRateCents / 100).toFixed(2);
  return (
    <Card className="mt-6 flex flex-wrap items-end gap-3 p-4">
      <div className="min-w-56 flex-1">
        <h2 className="text-sm font-bold">{t('mileage.rate')}</h2>
        <p className="text-xs text-fg-3">{t('mileage.rateHint')}</p>
      </div>
      {canManage ? (
        <>
          <Field label={t('mileage.ratePerKm')} className="w-32">{(p) => <Input {...p} inputMode="decimal" value={shown} onChange={(e) => setValue(e.target.value)} />}</Field>
          <Button
            variant="secondary"
            loading={save.isPending}
            disabled={value === null}
            onClick={() => {
              const cents = Math.round(Number((value ?? '').replace(',', '.')) * 100);
              if (!Number.isFinite(cents) || cents < 0) return;
              save.mutate({ input: { mileageRateCents: cents } });
            }}
          >
            {t('common.save')}
          </Button>
        </>
      ) : (
        <span className="text-sm tabular-nums">{formatMoney(q.data.mileageRateCents)} / km</span>
      )}
    </Card>
  );
}
